import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useEvents } from '../stores/events'
import { useAuth } from '../stores/auth'
import { supabase } from '@unvld/db'
import { toast } from 'sonner'
import { Plus, Trash2, ArrowLeft, Save, Camera, Upload, Repeat } from 'lucide-react'
import type { GuestType } from '@unvld/db'

interface TierForm {
  name: string
  guest_type: GuestType
  price_cents: number
  capacity: number | null
}

const DEFAULT_TIERS: TierForm[] = [
  { name: 'Paar', guest_type: 'couple', price_cents: 6000, capacity: null },
  { name: 'Einzelherr', guest_type: 'single_male', price_cents: 10000, capacity: null },
  { name: 'Einzeldame', guest_type: 'single_female', price_cents: 0, capacity: null },
]

const EVENT_TYPES = [
  { value: 'regular', label: 'Regulär' },
  { value: 'theme_night', label: 'Themenabend' },
  { value: 'couples_only', label: 'Nur Paare' },
  { value: 'ladies_night', label: 'Ladies Night' },
  { value: 'special', label: 'Special Event' },
]

const RECURRING_FREQUENCIES = [
  { value: 'weekly', label: 'Wöchentlich' },
  { value: 'biweekly', label: 'Zweiwöchentlich' },
  { value: 'monthly', label: 'Monatlich' },
]

function addDaysToDate(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

function generateRecurringDates(startDate: string, freq: string, until: string): string[] {
  const dates: string[] = []
  const start = new Date(startDate)
  const end = new Date(until)
  let current = new Date(start)

  const increment = freq === 'weekly' ? 7 : freq === 'biweekly' ? 14 : 0

  if (freq === 'monthly') {
    const dayOfMonth = start.getDate()
    current = new Date(start)
    current.setMonth(current.getMonth() + 1)
    current.setDate(dayOfMonth)
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0])
      current.setMonth(current.getMonth() + 1)
      current.setDate(dayOfMonth)
    }
  } else if (increment > 0) {
    current = addDaysToDate(start, increment)
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0])
      current = addDaysToDate(current, increment)
    }
  }

  return dates
}

export function EventCreatePage() {
  const navigate = useNavigate()
  const { org } = useAuth()
  const { createEvent } = useEvents()
  const [saving, setSaving] = useState(false)

  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    description: '',
    date: '',
    doors_open: '21:00',
    doors_close: '04:00',
    capacity: 100,
    event_type: 'regular',
    ratio_single_male_pct: 30,
    ratio_enforce: true,
    // Rules
    dress_code: '',
    age_min: 18,
    couples_only: false,
    member_only: false,
    // Recurring
    recurring_enabled: false,
    recurring_freq: 'weekly',
    recurring_until: '',
  })

  const [tiers, setTiers] = useState<TierForm[]>(DEFAULT_TIERS)

  // Cover image state
  const [coverImage, setCoverImage] = useState<File | null>(null)
  const [coverPreview, setCoverPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const updateForm = (key: string, value: any) => setForm((f) => ({ ...f, [key]: value }))

  const addTier = () => {
    setTiers((t) => [...t, { name: '', guest_type: 'couple', price_cents: 0, capacity: null }])
  }

  const removeTier = (index: number) => {
    setTiers((t) => t.filter((_, i) => i !== index))
  }

  const updateTier = (index: number, key: keyof TierForm, value: any) => {
    setTiers((t) => t.map((tier, i) => (i === index ? { ...tier, [key]: value } : tier)))
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte eine Bilddatei auswählen')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bild darf maximal 5 MB groß sein')
      return
    }
    setCoverImage(file)
    setCoverPreview(URL.createObjectURL(file))
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (!file || !file.type.startsWith('image/')) return
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bild darf maximal 5 MB groß sein')
      return
    }
    setCoverImage(file)
    setCoverPreview(URL.createObjectURL(file))
  }

  const uploadCoverImage = async (eventId: string): Promise<string | null> => {
    if (!coverImage || !org) return null
    const ext = coverImage.name.split('.').pop() || 'jpg'
    const path = `${org.id}/${eventId}/cover.${ext}`

    const { error } = await supabase.storage
      .from('event-images')
      .upload(path, coverImage, { upsert: true })

    if (error) {
      console.error('Image upload failed:', error)
      toast.error('Bild-Upload fehlgeschlagen')
      return null
    }

    const { data: { publicUrl } } = supabase.storage
      .from('event-images')
      .getPublicUrl(path)

    return publicUrl
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title || !form.date) {
      toast.error('Titel und Datum sind Pflichtfelder')
      return
    }

    if (form.recurring_enabled && !form.recurring_until) {
      toast.error('Bitte ein Enddatum für die Wiederholung angeben')
      return
    }

    setSaving(true)
    try {
      const dayOfWeek = new Date(form.date).getDay()
      const recurringRule = form.recurring_enabled
        ? { freq: form.recurring_freq, day: dayOfWeek, until: form.recurring_until }
        : null

      const event = await createEvent({
        org_id: org!.id,
        title: form.title,
        subtitle: form.subtitle || undefined,
        description: form.description || undefined,
        date: form.date,
        doors_open: form.doors_open,
        doors_close: form.doors_close || undefined,
        capacity: form.capacity,
        event_type: form.event_type as any,
        ratio_limits: {
          single_male_pct: form.ratio_single_male_pct,
          couple_pct: null,
          single_female_pct: null,
          enforce: form.ratio_enforce,
        },
        rules: {
          dress_code: form.dress_code || null,
          age_min: form.age_min,
          age_max: null,
          couples_only: form.couples_only,
          member_only: form.member_only,
        },
        recurring_rule: recurringRule,
        status: 'draft',
        price_tiers: tiers.map((t, i) => ({
          ...t,
          sort_order: i,
        })),
      })

      // Upload cover image after event creation
      if (coverImage) {
        const coverUrl = await uploadCoverImage(event.id)
        if (coverUrl) {
          await supabase
            .from('events')
            .update({ cover_image_url: coverUrl })
            .eq('id', event.id)
        }
      }

      // Create recurring child events
      if (form.recurring_enabled && form.recurring_until) {
        const recurringDates = generateRecurringDates(form.date, form.recurring_freq, form.recurring_until)

        for (const childDate of recurringDates) {
          const childEvent = await createEvent({
            org_id: org!.id,
            title: form.title,
            subtitle: form.subtitle || undefined,
            description: form.description || undefined,
            date: childDate,
            doors_open: form.doors_open,
            doors_close: form.doors_close || undefined,
            capacity: form.capacity,
            event_type: form.event_type as any,
            ratio_limits: {
              single_male_pct: form.ratio_single_male_pct,
              couple_pct: null,
              single_female_pct: null,
              enforce: form.ratio_enforce,
            },
            rules: {
              dress_code: form.dress_code || null,
              age_min: form.age_min,
              age_max: null,
              couples_only: form.couples_only,
              member_only: form.member_only,
            },
            recurring_rule: recurringRule,
            parent_event_id: event.id,
            status: 'draft',
            price_tiers: tiers.map((t, i) => ({
              ...t,
              sort_order: i,
            })),
          })

          // Upload same cover image for child events
          if (coverImage && childEvent) {
            const childCoverUrl = await uploadCoverImage(childEvent.id)
            if (childCoverUrl) {
              await supabase
                .from('events')
                .update({ cover_image_url: childCoverUrl })
                .eq('id', childEvent.id)
            }
          }
        }

        toast.success(`Event erstellt + ${recurringDates.length} Wiederholungen!`)
      } else {
        toast.success('Event erstellt!')
      }

      navigate(`/events/${event.id}`)
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Erstellen')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold text-white">Neues Event</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Cover Image Upload */}
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white">Cover-Bild</h2>
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className={`relative cursor-pointer border-2 border-dashed rounded-xl transition-colors overflow-hidden ${
              coverPreview
                ? 'border-indigo-500/50'
                : 'border-slate-700 hover:border-slate-500'
            }`}
          >
            {coverPreview ? (
              <div className="relative aspect-[2/1]">
                <img
                  src={coverPreview}
                  alt="Cover Vorschau"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <div className="flex items-center gap-2 text-white text-sm">
                    <Upload className="w-4 h-4" />
                    Bild ändern
                  </div>
                </div>
              </div>
            ) : (
              <div className="aspect-[2/1] flex flex-col items-center justify-center text-slate-500">
                <Camera className="w-10 h-10 mb-3" />
                <p className="text-sm">Klicken oder Bild hierher ziehen</p>
                <p className="text-xs text-slate-600 mt-1">JPG, PNG, WebP · max. 5 MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
          </div>
        </section>

        {/* Basic Info */}
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white">Grunddaten</h2>

          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Titel *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => updateForm('title', e.target.value)}
              placeholder="z.B. Saturday Night Passion"
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Untertitel</label>
            <input
              type="text"
              value={form.subtitle}
              onChange={(e) => updateForm('subtitle', e.target.value)}
              placeholder="z.B. Dresscode: Schwarz/Rot"
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Beschreibung</label>
            <textarea
              value={form.description}
              onChange={(e) => updateForm('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              placeholder="Was erwartet eure Gäste?"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Event-Typ</label>
              <select
                value={form.event_type}
                onChange={(e) => updateForm('event_type', e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Kapazität *</label>
              <input
                type="number"
                value={form.capacity}
                onChange={(e) => updateForm('capacity', parseInt(e.target.value) || 0)}
                min={1}
                className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
          </div>
        </section>

        {/* Date & Time */}
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white">Datum & Zeit</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Datum *</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => updateForm('date', e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Einlass ab</label>
              <input
                type="time"
                value={form.doors_open}
                onChange={(e) => updateForm('doors_open', e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Einlass bis</label>
              <input
                type="time"
                value={form.doors_close}
                onChange={(e) => updateForm('doors_close', e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </section>

        {/* Price Tiers */}
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Preiskategorien</h2>
            <button
              type="button"
              onClick={addTier}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-400 hover:text-indigo-300 border border-indigo-500/30 rounded-lg hover:bg-indigo-500/10 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Kategorie
            </button>
          </div>

          {tiers.map((tier, index) => (
            <div key={index} className="flex flex-wrap items-end gap-3 p-3 bg-slate-800/50 rounded-lg">
              <div className="flex-1">
                <label className="block text-xs text-slate-400 mb-1">Name</label>
                <input
                  type="text"
                  value={tier.name}
                  onChange={(e) => updateTier(index, 'name', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="w-36">
                <label className="block text-xs text-slate-400 mb-1">Typ</label>
                <select
                  value={tier.guest_type}
                  onChange={(e) => updateTier(index, 'guest_type', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="couple">Paar</option>
                  <option value="single_male">Einzelherr</option>
                  <option value="single_female">Einzeldame</option>
                  <option value="group">Gruppe</option>
                </select>
              </div>
              <div className="w-28">
                <label className="block text-xs text-slate-400 mb-1">Preis (€)</label>
                <input
                  type="number"
                  value={tier.price_cents / 100}
                  onChange={(e) => updateTier(index, 'price_cents', Math.round(parseFloat(e.target.value || '0') * 100))}
                  min={0}
                  step={0.5}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <button
                type="button"
                onClick={() => removeTier(index)}
                className="p-2 text-slate-500 hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </section>

        {/* Ratio Control */}
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white">Gäste-Ratio</h2>
          <p className="text-sm text-slate-400">
            Steuere automatisch das Verhältnis der Gästetypen. Wenn das Limit erreicht ist,
            werden keine weiteren Tickets für diese Kategorie verkauft.
          </p>

          <div className="flex items-center gap-3 mb-4">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.ratio_enforce}
                onChange={(e) => updateForm('ratio_enforce', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-700 peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
            </label>
            <span className="text-sm text-slate-300">Ratio-Kontrolle aktivieren</span>
          </div>

          {form.ratio_enforce && (
            <div className="max-w-xs">
              <label className="block text-sm text-slate-400 mb-1.5">
                Max. Einzelherren-Anteil: {form.ratio_single_male_pct}%
              </label>
              <input
                type="range"
                min={0}
                max={100}
                value={form.ratio_single_male_pct}
                onChange={(e) => updateForm('ratio_single_male_pct', parseInt(e.target.value))}
                className="w-full accent-indigo-500"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          )}
        </section>

        {/* Rules / Regeln */}
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white">Regeln</h2>
          <p className="text-sm text-slate-400">
            Definiere Dresscode, Altersgrenze und Zugangsregeln für dieses Event.
          </p>

          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Dresscode</label>
            <input
              type="text"
              value={form.dress_code}
              onChange={(e) => updateForm('dress_code', e.target.value)}
              placeholder="z.B. Elegant, keine Jeans"
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="max-w-xs">
            <label className="block text-sm text-slate-400 mb-1.5">Mindestalter</label>
            <input
              type="number"
              value={form.age_min}
              onChange={(e) => updateForm('age_min', parseInt(e.target.value) || 18)}
              min={18}
              max={99}
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.couples_only}
                onChange={(e) => updateForm('couples_only', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-700 peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
            </label>
            <span className="text-sm text-slate-300">Nur Paare</span>
          </div>

          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.member_only}
                onChange={(e) => updateForm('member_only', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-700 peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
            </label>
            <span className="text-sm text-slate-300">Nur Mitglieder</span>
          </div>
        </section>

        {/* Recurring Events */}
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Repeat className="w-5 h-5 text-indigo-400" />
            <h2 className="text-lg font-semibold text-white">Wiederkehrend</h2>
          </div>

          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.recurring_enabled}
                onChange={(e) => updateForm('recurring_enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-700 peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
            </label>
            <span className="text-sm text-slate-300">Wiederkehrendes Event</span>
          </div>

          {form.recurring_enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Frequenz</label>
                <select
                  value={form.recurring_freq}
                  onChange={(e) => updateForm('recurring_freq', e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {RECURRING_FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Wiederholen bis</label>
                <input
                  type="date"
                  value={form.recurring_until}
                  onChange={(e) => updateForm('recurring_until', e.target.value)}
                  min={form.date || undefined}
                  className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {form.date && form.recurring_until && (
                <div className="col-span-2">
                  <p className="text-xs text-slate-500">
                    {generateRecurringDates(form.date, form.recurring_freq, form.recurring_until).length} zusätzliche Events werden erstellt
                  </p>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Submit */}
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium rounded-xl transition-colors"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Event erstellen
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 text-slate-400 hover:text-white transition-colors"
          >
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  )
}
